import { pluralizeWithCount } from "@common/helpers/string";
import type {
    BoardLock,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { getCellKey } from "@design-system/Board/boardCells";
import type { DemoTask } from "./fixtures/demoTasks";
import {
    applyTaskChange,
    describeTaskChange,
    type TaskBoardChange,
} from "./taskBoardActions";

type GetPlacements = (task: DemoTask) => readonly BoardPlacement[];

/** The board's move rules that the action set honours, as the Controls panel turns them on. */
export interface TaskChangeRules {
    /** A locked task takes no change that would move it on the board. */
    getLock?: (task: DemoTask) => BoardLock | null;
    /** Why a task breaks a rule the board keeps for moves in its current columns, or null. */
    getRuleBreak?: (
        task: DemoTask,
        getPlacements: GetPlacements,
    ) => string | null;
}

/** The tasks a change may update, and why it skips each of the rest. */
export interface TaskChangeOutcome {
    updatedIds: ReadonlySet<string>;
    refusalReasons: readonly string[];
}

const LOCKED_REASON = "Locked tasks can't move";

const getCellKeys = (task: DemoTask, getPlacements: GetPlacements) =>
    getPlacements(task).map(getCellKey).sort().join(" ");

interface RefusalArgs {
    task: DemoTask;
    change: TaskBoardChange;
    rules: TaskChangeRules;
    getPlacements: GetPlacements;
}

const getRefusalReason = ({
    task,
    change,
    rules: { getLock, getRuleBreak },
    getPlacements,
}: RefusalArgs) => {
    const [changedTask = task] = applyTaskChange({
        tasks: [task],
        taskIds: new Set([task.id]),
        change,
    });
    const lock = getLock?.(task);
    if (
        lock &&
        getCellKeys(task, getPlacements) !==
            getCellKeys(changedTask, getPlacements)
    ) {
        return lock.reason ?? LOCKED_REASON;
    }
    // A task that already breaks a rule keeps taking edits; only a change
    // that breaks it is refused.
    if (
        getRuleBreak === undefined ||
        getRuleBreak(task, getPlacements) !== null
    ) {
        return null;
    }
    return getRuleBreak(changedTask, getPlacements);
};

interface CheckTaskChangeArgs {
    tasks: readonly DemoTask[];
    taskIds: ReadonlySet<string>;
    change: TaskBoardChange;
    rules: TaskChangeRules;
    /** The board's current columns and lanes: a lock refuses a change that moves a task across them. */
    getPlacements: GetPlacements;
}

/** Which of `taskIds` `change` may update under `rules`, and why the rest can't change. */
export const checkTaskChange = ({
    tasks,
    taskIds,
    change,
    rules,
    getPlacements,
}: CheckTaskChangeArgs): TaskChangeOutcome => {
    const updatedIds = new Set<string>();
    const refusalReasons: string[] = [];
    for (const task of tasks) {
        if (!taskIds.has(task.id)) {
            continue;
        }
        const reason = getRefusalReason({
            task,
            change,
            rules,
            getPlacements,
        });
        if (reason === null) {
            updatedIds.add(task.id);
        } else {
            refusalReasons.push(reason);
        }
    }
    return { updatedIds, refusalReasons };
};

/**
 * The toast after a checked change: `describeTaskChange` when it reached
 * every task, else "Updated 3 tasks, 1 couldn't change. Canceled tasks
 * can't be moved." with each distinct reason once.
 */
export const describeTaskUpdate = (
    change: TaskBoardChange,
    { updatedIds, refusalReasons }: TaskChangeOutcome,
) => {
    if (refusalReasons.length === 0) {
        return describeTaskChange(change, updatedIds.size);
    }
    const reasons = [...new Set(refusalReasons)]
        .map((reason) => `${reason}.`)
        .join(" ");
    return updatedIds.size === 0
        ? `${pluralizeWithCount("task", refusalReasons.length)} couldn't change. ${reasons}`
        : `Updated ${pluralizeWithCount("task", updatedIds.size)}, ${refusalReasons.length} couldn't change. ${reasons}`;
};
